import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import {
  selectPermissions,
  selectRoles,
  selectPermissionsAccountId,
  selectPermissionsLoading,
  selectPermissionsLoaded,
  selectPermissionsError,
  fetchPermissions,
} from '../features/permissions/permissionsSlice';
import {
  hasPermission as checkPermission,
  hasAnyPermission as checkAnyPermission,
  hasAllPermissions as checkAllPermissions,
} from '../constants/permissions';

/**
 * Hook to access and manage user permissions
 *
 * @returns {Object} Permission utilities and state
 */
const usePermissions = () => {
  const dispatch = useDispatch();

  // Select state from Redux
  const permissions = useSelector(selectPermissions);
  const roles = useSelector(selectRoles);
  const permissionsAccountId = useSelector(selectPermissionsAccountId);
  const loading = useSelector(selectPermissionsLoading);
  const loaded = useSelector(selectPermissionsLoaded);
  const error = useSelector(selectPermissionsError);
  const user = useSelector((state) => state.account.user);

  /**
   * Fetch permissions if:
   * - User is logged in AND
   * - (Permissions not loaded OR permissions belong to a different user) AND
   * - Not currently loading
   *
   * This prevents race conditions where User B could inherit User A's permissions
   * if User A's fetch completes after logout but before User B logs in.
   */
  useEffect(() => {
    // Validate user.accountId is truthy to prevent infinite loop:
    // If accountId is undefined, fetchPermissions stores null, but
    // null !== undefined triggers another fetch indefinitely
    const needsFetch = user?.accountId && !loading && (
      !loaded || permissionsAccountId !== user.accountId
    );
    if (needsFetch) {
      dispatch(fetchPermissions(user.accountId));
    }
  }, [user, loaded, loading, permissionsAccountId, dispatch]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission) => {
      return checkPermission(permissions, permission);
    },
    [permissions]
  );

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (requiredPermissions) => {
      return checkAnyPermission(permissions, requiredPermissions);
    },
    [permissions]
  );

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermission = useCallback(
    (requiredPermissions) => {
      return checkAllPermissions(permissions, requiredPermissions);
    },
    [permissions]
  );

  /**
   * Check if user has a specific role by name
   */
  const hasRole = useCallback(
    (roleName) => {
      return roles.some((role) => role.name === roleName);
    },
    [roles]
  );

  /**
   * Check if user is an admin (has admin:* or global * permission)
   * Note: admin:read is view-only and does NOT grant admin privileges
   */
  const isAdmin = useCallback(() => {
    return hasPermission('admin:*');
  }, [hasPermission]);

  /**
   * Refresh permissions from the server
   */
  const refresh = useCallback(() => {
    if (user?.accountId) {
      dispatch(fetchPermissions(user.accountId));
    }
  }, [dispatch, user]);

  return {
    // State
    permissions,
    roles,
    loading,
    loaded,
    error,

    // Utility functions
    hasPermission,
    hasAnyPermission,
    hasAllPermission,
    hasRole,
    isAdmin,
    refresh,
  };
};

export default usePermissions;
